import { useState, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { OLSPredictionInput, OLSPredictionOutput } from '@otw/models/ols'
import { olsApi } from '@/core/api/modules/olsApi'

/**
 * Hook for making OLS price predictions.
 */
export const useOLSPrediction = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const predictPrice = useCallback(
    async (input: OLSPredictionInput): Promise<OLSPredictionOutput> => {
      setIsLoading(true)
      setError(null)

      try {
        return await olsApi.pricing.predictPrice(input)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(errorMessage)
        // Return fallback result
        return {
          buyInPrice: null,
          sellPrice: null,
          modelVersionId: null,
          categoryId: null,
          usedFallback: true,
          fallbackReason: errorMessage,
        }
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  const predictPricesBatch = useCallback(
    async (inputs: OLSPredictionInput[]): Promise<OLSPredictionOutput[]> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await olsApi.pricing.predictPriceBatch(inputs)
        return response.predictions
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(errorMessage)
        // Return fallback results
        return inputs.map(() => ({
          buyInPrice: null,
          sellPrice: null,
          modelVersionId: null,
          categoryId: null,
          usedFallback: true,
          fallbackReason: errorMessage,
        }))
      } finally {
        setIsLoading(false)
      }
    },
    []
  )

  return {
    predictPrice,
    predictPricesBatch,
    isLoading,
    error,
  }
}

/**
 * Hook for getting OLS prediction with React Query caching.
 */
export const useOLSPredictionQuery = (
  input: OLSPredictionInput | null,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      'ols_prediction',
      input?.make,
      input?.model,
      input?.type,
      input?.size,
      input?.fuel,
      input?.year,
      input?.mileage,
    ],
    queryFn: async () => {
      if (!input) return null
      return await olsApi.pricing.predictPrice(input)
    },
    enabled: options?.enabled !== false && input !== null,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

/**
 * Mutation hook for batch predictions.
 */
export const useOLSPredictionBatch = () => {
  return useMutation({
    mutationFn: async (inputs: OLSPredictionInput[]) => {
      const response = await olsApi.pricing.predictPriceBatch(inputs)
      return response.predictions
    },
  })
}

interface OLSPredictionGridInput {
  make: string
  model: string
  type: string
  size: string
  fuel: 'Gas' | 'Diesel'
  years: number[]
  mileages: number[]
}

interface OLSPredictionGridResult {
  predictions: Array<{
    year: number
    mileage: number
    buyInPrice: number | null
    sellPrice: number | null
    categoryId: string | null
    usedFallback: boolean
  }>
  hasActiveModel: boolean
  categoryId: string | null
}

/**
 * Hook to fetch OLS predictions for a grid of year/mileage combinations.
 * Used to overlay OLS prediction lines on formula charts.
 */
export const useOLSPredictionGrid = (input: OLSPredictionGridInput) => {
  return useQuery({
    queryKey: ['ols_prediction_grid', input],
    queryFn: async (): Promise<OLSPredictionGridResult> => {
      // Generate all year/mileage combinations
      const combinations: OLSPredictionInput[] = input.years.flatMap(year =>
        input.mileages.map(mileage => ({
          make: input.make,
          model: input.model,
          type: input.type,
          size: input.size,
          fuel: input.fuel,
          year,
          mileage,
        }))
      )

      // Use the API
      const response = await olsApi.pricing.predictPriceBatch(combinations)
      const predictions = response.predictions

      // Check if we have an active model (any prediction that didn't use fallback)
      const hasActiveModel = predictions.some(p => !p.usedFallback && p.categoryId !== null)
      const categoryId = predictions.find(p => p.categoryId)?.categoryId || null

      return {
        predictions: predictions.map((pred, idx) => ({
          year: combinations[idx].year,
          mileage: combinations[idx].mileage,
          buyInPrice: pred.buyInPrice,
          sellPrice: pred.sellPrice,
          categoryId: pred.categoryId,
          usedFallback: pred.usedFallback,
        })),
        hasActiveModel,
        categoryId,
      }
    },
    enabled:
      !!input.make &&
      !!input.model &&
      !!input.type &&
      !!input.size &&
      input.years.length > 0 &&
      input.mileages.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to check if OLS model exists for a category.
 */
export const useOLSModelAvailability = (
  make: string,
  model: string,
  type: string,
  size: string,
  fuel: 'Gas' | 'Diesel'
) => {
  return useQuery({
    queryKey: ['ols_model_availability', make, model, type, size, fuel],
    queryFn: async (): Promise<{ available: boolean; categoryId: string | null }> => {
      try {
        return await olsApi.pricing.checkModelAvailability({ make, model, type, size, fuel })
      } catch (error) {
        return { available: false, categoryId: null }
      }
    },
    enabled: !!make && !!model && !!type && !!size,
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}
